<script setup>
import { computed, unref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  /** Ref to `DashboardEasyDataTable` (pass the ref object, e.g. `workflowsTableRef`). */
  wrapperRef: {
    type: Object,
    default: null,
  },
  /** Total rows shown in the table (e.g. `tableItems.length`). Footer stays hidden when 0. */
  itemCount: {
    type: Number,
    default: 0,
  },
  /** Translated label next to the rows-per-page control (e.g. "Workflows per page:"). */
  rowsPerPageLabel: {
    type: String,
    required: true,
  },
  /**
   * Optional i18n key for the compact range line (defaults to `EASY_DATA_TABLE_FOOTER.ITEM_RANGE`).
   * Pass `PAGINATION_FOOTER.SHOWING` if you need the long "Showing ... items" sentence instead.
   */
  rangeInfo: {
    type: String,
    default: '',
  },
  /** Optional override for `aria-label` on the nav region (defaults to `EASY_DATA_TABLE_FOOTER.NAV_LABEL`). */
  navAriaLabel: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const { formatCompactNumber, formatFullNumber } = useNumberFormatter();
const selectId = useId();

const dashboardWrapper = computed(() => unref(props.wrapperRef) ?? null);
const innerTable = computed(
  () => dashboardWrapper.value?.getTableRef?.() ?? null
);

const totalItems = computed(() =>
  innerTable.value ? unref(innerTable.value.clientItemsLength) : 0
);
const currentPage = computed(() =>
  innerTable.value ? unref(innerTable.value.currentPaginationNumber) : 1
);
const itemsPerPage = computed(() =>
  innerTable.value ? unref(innerTable.value.rowsPerPageActiveOption) : 25
);
const pageFirst = computed(() =>
  innerTable.value ? unref(innerTable.value.currentPageFirstIndex) : 1
);
const pageLast = computed(() =>
  innerTable.value ? unref(innerTable.value.currentPageLastIndex) : 0
);
const rowsOptions = computed(() => {
  const inner = innerTable.value;
  if (!inner) return [10, 25, 50, 100];
  const opts = unref(inner.rowsPerPageOptions);
  return Array.isArray(opts) && opts.length ? opts : [10, 25, 50, 100];
});
const rowsActive = computed(() =>
  innerTable.value ? unref(innerTable.value.rowsPerPageActiveOption) : 25
);

const totalPages = computed(() => {
  const ipp = itemsPerPage.value || 25;
  const ti = totalItems.value;
  return Math.max(1, Math.ceil(ti / ipp));
});

const isFirstPage = computed(() => currentPage.value <= 1);
const isLastPage = computed(() => currentPage.value >= totalPages.value);

const rangeText = computed(() => {
  const translationKey = props.rangeInfo || 'EASY_DATA_TABLE_FOOTER.ITEM_RANGE';
  return t(translationKey, {
    startItem: formatFullNumber(pageFirst.value),
    endItem: formatFullNumber(pageLast.value),
    totalItems: formatCompactNumber(totalItems.value),
  });
});

const navLabel = computed(
  () => props.navAriaLabel || t('EASY_DATA_TABLE_FOOTER.NAV_LABEL')
);

const visible = computed(() => props.itemCount > 0 && innerTable.value != null);

const onRowsChange = e => {
  const next = Number(e?.target?.value);
  if (!Number.isFinite(next)) return;
  dashboardWrapper.value?.updateRowsPerPageActiveOption?.(next);
};

const changePage = page => {
  if (page >= 1 && page <= totalPages.value) {
    dashboardWrapper.value?.updatePage?.(page);
  }
};
</script>

<template>
  <div
    v-if="visible"
    class="flex h-[54px] min-h-[54px] max-h-[54px] w-full min-w-0 shrink-0 flex-nowrap items-center justify-end gap-x-4 overflow-x-auto border-t border-n-weak bg-n-slate-2 px-4 py-0 dark:border-n-strong md:px-6"
    role="navigation"
    :aria-label="navLabel"
  >
    <div class="flex shrink-0 items-center gap-2">
      <label
        class="m-0 flex shrink-0 items-center self-center whitespace-nowrap text-sm leading-none text-n-slate-11"
        :for="selectId"
      >
        {{ rowsPerPageLabel }}
      </label>
      <div class="inline-flex h-[30px] shrink-0 items-center gap-px rounded-md">
        <select
          :id="selectId"
          :value="rowsActive"
          class="h-full min-h-0 max-w-[3.25rem] min-w-0 cursor-pointer appearance-none border-0 bg-transparent py-0 ps-1 pe-0 text-center text-sm tabular-nums leading-none text-n-slate-12 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-brand"
          @change="onRowsChange"
        >
          <option v-for="opt in rowsOptions" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
        <span
          class="pointer-events-none inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center text-n-slate-11 i-lucide-chevrons-up-down"
          aria-hidden="true"
        />
      </div>
    </div>

    <span
      class="flex shrink-0 items-center self-center whitespace-nowrap text-sm tabular-nums leading-none text-n-slate-11"
    >
      {{ rangeText }}
    </span>

    <div class="flex shrink-0 items-center gap-0.5">
      <Button
        variant="ghost"
        color="slate"
        class="!h-[30px] !min-h-[30px] !w-[30px] !min-w-[30px] !shrink-0 !gap-0 !p-0"
        :disabled="isFirstPage"
        @click="changePage(currentPage - 1)"
      >
        <template #icon>
          <span
            class="inline-block h-[20px] w-[20px] shrink-0 i-lucide-chevron-left"
          />
        </template>
      </Button>
      <Button
        variant="ghost"
        color="slate"
        class="!h-[30px] !min-h-[30px] !w-[30px] !min-w-[30px] !shrink-0 !gap-0 !p-0"
        :disabled="isLastPage"
        @click="changePage(currentPage + 1)"
      >
        <template #icon>
          <span
            class="inline-block h-[20px] w-[20px] shrink-0 i-lucide-chevron-right"
          />
        </template>
      </Button>
    </div>
  </div>
</template>
